import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useContrast, useShellTheme } from "@/lib/contrast-context";
import { Button } from "@/components/ui/button";
import {
  PlusIcon, HomeIcon, FamilyIcon, FinanceIcon, MessageIcon,
  CalendarIcon, MarketplaceIcon, DocumentIcon, ConsentIcon, SettingsIcon,
  MenuIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon,
  OverviewIcon, AcademicsIcon, AttendanceIcon, WalletIcon,
  HealthIcon, TransportIcon, TimelineIcon, DocumentIcon as DocTabIcon,
  BrandMarkIcon, SearchIcon, BellIcon, PlusIcon as PlusLucide,
  MorningIcon, AfternoonIcon, EveningIcon,
} from "./icons";
import { Plus as PlusLucide2, Wallet as WalletLucide, Contrast } from "lucide-react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { LearnerOnboardingWizard } from "@/features/learner-onboarding-wizard";
import { TriSpherePayTopUp } from "@/components/schoolos-pay-topup";
import { ParentCalendar } from "@/features/parent-calendar";
import { ParentMessages } from "@/features/parent-messages";
import { ParentCampusExchange } from "@/features/parent-campus-exchange";
import { ParentDocuments } from "@/features/parent-documents";
import {
  C, ACCENT_GRADIENT, type Student, type FeeTerm, type Ledger,
  type Visit, type ParentWallet, type Checkpoint, type NavSection, type ChildTab,
  type Insight, childColor, getGreeting,
} from "./tokens";
import { HomeView } from "./home-view";
import { FamilyView } from "./family-view";
import { FinanceSummary, MoneyView } from "./finance-view";
import { ConsentView } from "./consent-view";
import { ChildWorkspace } from "./child-workspace";
import { SettingsView, TimetableModal, AssignmentsModal, MessageModal, WalletModal, SavedListingsModal, EventsModal, ReportModal } from "./modals";
import { AddChildFromSchoolDialog } from "./add-child-dialog";

const EASE = [0.4, 0, 0.2, 1] as const;

function fmtUGXShort(n: number): string {
  if (n >= 1_000_000) return `UGX ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `UGX ${(n / 1_000).toFixed(0)}K`;
  return `UGX ${n}`;
}

export function ParentDashboard() {
  const { user } = useAuth();
  const { highContrast: hc, toggle: toggleContrast } = useContrast();
  const qc = useQueryClient();

  const [showWizard, setShowWizard] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [navSection, setNavSection] = useState<NavSection>("home");
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [childTab, setChildTab] = useState<ChildTab>("overview");

  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 200], [1, 0.85]);
  const heroScale = useTransform(scrollY, [0, 200], [1, 0.96]);
  const heroHeight = useTransform(scrollY, [0, 200], [1, 1]);

  const { data: rawStudents } = useQuery({
    queryKey: ["students", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("students").select("*,schools(name)").eq("guardian_id", user!.id).order("full_name");
      return (data ?? []).map((s: any) => ({ ...s, school_name: s.schools?.name ?? s.school_name ?? null }));
    },
  });
  const fetchedStudents = rawStudents ?? [];
  const students: Student[] = fetchedStudents;

  const { data: fees = [] } = useQuery<FeeTerm[]>({ queryKey: ["fees", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("student_fee_terms").select("*"); return data ?? []; } });
  const { data: ledger = [] } = useQuery<Ledger[]>({ queryKey: ["ledger", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("parent_ledger").select("*").eq("guardian_id", user!.id).order("occurred_at", { ascending: false }).limit(80); return data ?? []; } });
  const { data: visits = [] } = useQuery<Visit[]>({ queryKey: ["visits", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("health_visits").select("*").order("visit_date", { ascending: false }).limit(40); return data ?? []; } });
  const { data: parentWallet } = useQuery<ParentWallet | null>({ queryKey: ["parent_wallet", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("wallets").select("id,balance_ugx").eq("owner_id", user!.id).eq("owner_type", "parent").maybeSingle(); return data ?? null; } });
  const { data: studentWallets = [] } = useQuery({ queryKey: ["student_wallets", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("wallets").select("id,owner_id,balance_ugx,wallet_type").eq("owner_type", "student").in("owner_id", fetchedStudents.map((s: any) => s.id)); return data ?? []; } });
  const { data: checkpoints = [] } = useQuery<Checkpoint[]>({ queryKey: ["checkpoints", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("attendance_checkpoints").select("*").eq("guardian_id", user!.id).order("scanned_at", { ascending: false }).limit(40); return data ?? []; } });
  const { data: insights = [] } = useQuery<Insight[]>({ queryKey: ["insights", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("ai_insights").select("*").eq("guardian_id", user!.id).eq("status", "open").order("created_at", { ascending: false }).limit(10); return data ?? []; } });
  const { data: notifications = [] } = useQuery({ queryKey: ["notifications", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("notifications").select("*").eq("user_id", user!.id).order("created_at", { ascending: false }).limit(20); return data ?? []; } });
  const { data: calendarEvents = [] } = useQuery({ queryKey: ["calendar_events", user?.id], enabled: !!user, queryFn: async () => { const { data } = await supabase.from("calendar_events").select("*").eq("user_id", user!.id).order("start_time", { ascending: true }).limit(10); return data ?? []; } });

  useEffect(() => { if (!activeChildId && students.length > 0) setActiveChildId(students[0].id); }, [activeChildId, students]);

  const go = (s: NavSection) => { setNavSection(s); };
  const goChild = (id: string, tab: ChildTab = "overview") => { setActiveChildId(id); setNavSection("child"); setChildTab(tab); };

  const { text: greetText, icon: greetIcon } = getGreeting();
  const firstName = (user?.user_metadata?.full_name ?? "Parent").split(" ")[0];
  const activeChild = students.find(s => s.id === activeChildId) ?? students[0];
  const outstandingFees = fees.reduce((s, f) => s + Math.max(0, f.total_ugx - f.paid_ugx), 0);
  const unreadCount = (notifications as any[] ?? []).filter((n: any) => !n.read_at).length;
  const schoolCount = new Set(students.map(s => s.school_name).filter(Boolean)).size;

  const CHILD_TABS: { k: ChildTab; label: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }> }[] = [
    { k: "overview", label: "Overview", icon: OverviewIcon },
    { k: "academics", label: "Academics", icon: AcademicsIcon },
    { k: "attendance", label: "Attendance", icon: AttendanceIcon },
    { k: "finance", label: "Finance", icon: WalletIcon },
    { k: "health", label: "Health", icon: HealthIcon },
    { k: "transport", label: "Transport", icon: TransportIcon },
    { k: "messages", label: "Messages", icon: MessageIcon },
    { k: "documents", label: "Docs", icon: DocTabIcon },
    { k: "timeline", label: "Timeline", icon: TimelineIcon },
  ];

  const NAV_ITEMS: { k: NavSection; label: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; badge?: number }[] = [
    { k: "home", label: "Home", icon: HomeIcon },
    { k: "family", label: "Household", icon: FamilyIcon },
    { k: "finance", label: "Finance", icon: FinanceIcon, badge: fees.filter(f => f.total_ugx > f.paid_ugx).length || undefined },
    { k: "messages", label: "Inbox", icon: MessageIcon, badge: unreadCount || undefined },
    { k: "calendar", label: "Planner", icon: CalendarIcon },
    { k: "marketplace", label: "Exchange", icon: MarketplaceIcon },
    { k: "documents", label: "Vault", icon: DocumentIcon },
    { k: "consent", label: "Consent", icon: ConsentIcon },
  ];

  const GreetIcon = greetIcon === "morning" ? MorningIcon : greetIcon === "afternoon" ? AfternoonIcon : EveningIcon;

  const payTrigger = (
    <motion.button
      whileTap={{ scale: 0.95 }}
      className="flex items-center gap-1.5 rounded-none px-3.5 py-2 text-xs font-bold uppercase tracking-wider text-white transition-all"
      style={{ background: "#1C1917", border: "none", boxShadow: "0 2px 12px rgba(123,20,22,0.15)" }}
    >
      <PlusLucide2 className="h-3.5 w-3.5" />Top up
    </motion.button>
  );

  return (
    <div className="min-h-screen relative" style={{ background: "#F5EFE4" }}>
      <div className="relative" style={{ background: "#F5EFE4" }}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}>
          {/* Brand row */}
          <div className="flex items-center justify-between mb-6 sm:mb-7">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-[10px] flex items-center justify-center" style={{ background: "#7B1416" }}>
                <BrandMarkIcon className="h-5 w-5" accent="#C4A882" style={{ color: "#C4A882" }} />
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.22em]" style={{ color: "#7B1416" }}>TriSphere</p>
                <p className="text-sm font-semibold tracking-tight" style={{ color: "#1C1917" }}>Family Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleContrast()}
                aria-pressed={hc}
                aria-label={hc ? "Disable high contrast mode" : "Enable high contrast mode"}
                title={hc ? "High contrast: ON" : "High contrast: OFF"}
                className="h-9 w-9 rounded-[10px] flex items-center justify-center transition-all"
                style={{
                  background: hc ? "#1C1917" : "#EDE4D4",
                  border: hc ? "2px solid #000000" : "1px solid #E2D6C0",
                }}
              >
                <Contrast className="h-4 w-4" style={{ color: hc ? "#FFFFFF" : "#1C1917" }} strokeWidth={2.5} />
              </button>
              <button className="relative h-9 w-9 rounded-[10px] flex items-center justify-center transition-colors hover:bg-black/5" style={{ background: "#EDE4D4", border: "1px solid #E2D6C0" }}>
                <BellIcon className="h-4 w-4" style={{ color: "#1C1917" }} />
                {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full" style={{ background: "#7B1416" }} />}
              </button>
              <div className="flex items-center gap-2 rounded-[10px] px-3 py-2" style={{ background: "#EDE4D4", border: "1px solid #E2D6C0" }}>
                <WalletLucide className="h-4 w-4" style={{ color: "#7B1416" }} />
                <span className="text-xs font-semibold" style={{ color: "#1C1917" }}>{fmtUGXShort(parentWallet?.balance_ugx ?? 0)}</span>
              </div>
              <TriSpherePayTopUp walletId={parentWallet?.id} students={students} fees={fees} trigger={payTrigger} />
            </div>
          </div>

          {/* Greeting */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.05 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <GreetIcon className="h-3.5 w-3.5" style={{ color: "#7B1416" }} />
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: "#7B1416" }}>{greetText}</span>
              <span style={{ color: "#C4A882" }}>·</span>
              <span className="text-[11px] font-medium" style={{ color: "#8B6F47" }}>
                {new Date().toLocaleDateString("en-UG", { weekday: "short", day: "numeric", month: "short" })}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-[1.1]" style={{ color: "#1C1917", fontFamily: 'Georgia, serif' }}>
              {firstName}
            </h1>
            <p className="mt-2 text-sm font-medium" style={{ color: "#8B6F47" }}>
              {students.length > 0
                ? `${students.length} ${students.length === 1 ? "child" : "children"} · ${schoolCount} ${schoolCount === 1 ? "school" : "schools"}`
                : "Welcome — link your child to get started"}
            </p>

            {/* Live child status chips */}
            {students.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4, ease: EASE }}
                className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar"
                style={{ scrollbarWidth: "none" }}
              >
                {students.slice(0, 4).map((s, i) => {
                  const color = childColor(students, s.id);
                  const isPresent = s.status_dot === "green";
                  return (
                    <motion.button
                      key={s.id}
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + i * 0.05 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => goChild(s.id)}
                      className="flex items-center gap-2 rounded-none pl-1.5 pr-3 py-1.5 whitespace-nowrap transition-all hover:bg-black/[0.03]"
                      style={{ background: "#EDE4D4", border: "1px solid #E2D6C0" }}
                    >
                      <span className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: color }}>{s.full_name.charAt(0)}</span>
                      <span className="text-[11px] font-semibold" style={{ color: "#1C1917" }}>{s.full_name.split(" ")[0]}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: isPresent ? "#C4A882" : "#C4A882" }} />
                        <span className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: isPresent ? "#8B6F47" : "#8B6F47" }}>{s.status_label ?? "Active"}</span>
                      </span>
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          NAV BAR — sticky, glassmorphic, snap-scroll on tablet/desktop
          Hidden on mobile (bottom nav handles navigation there)
         ═══════════════════════════════════════════════════════ */}
      <header
        className="sticky top-0 z-30 border-b hidden lg:block"
        style={{ background: "rgba(245,239,228,0.85)", backdropFilter: "blur(48px) saturate(180%)", WebkitBackdropFilter: "blur(48px) saturate(180%)", borderColor: "rgba(28,25,23,0.10)" }}
      >
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-3" style={{ scrollbarWidth: "none", scrollSnapType: "x proximity" }}>
            {NAV_ITEMS.map(n => {
              const active = navSection === n.k && navSection !== "child";
              return (
                <button
                  key={n.k}
                  onClick={() => go(n.k)}
                  className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold whitespace-nowrap transition-all"
                  style={{
                    background: active ? "#1C1917" : "transparent",
                    color: active ? "#fff" : "#3C3530",
                    border: `1px solid ${active ? "transparent" : "#E2D6C0"}`,
                    scrollSnapAlign: "start",
                  }}
                >
                  <n.icon className="h-3.5 w-3.5" style={{ color: active ? "#fff" : "#3C3530" }} />
                  {n.label}
                  {n.badge ? (
                    <span className="ml-0.5 h-4 min-w-4 rounded-none text-[9px] font-bold grid place-items-center px-1" style={{ background: active ? "rgba(196,168,130,0.3)" : "#7B1416", color: "#fff" }}>{n.badge}</span>
                  ) : null}
                </button>
              );
            })}
            {students.map(s => {
              const color = childColor(students, s.id);
              const isActive = navSection === "child" && activeChildId === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => goChild(s.id)}
                  className="flex items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-bold whitespace-nowrap transition-all"
                  style={{
                    background: isActive ? `${color}14` : "transparent",
                    color: isActive ? color : "#3C3530",
                    border: `1px solid ${isActive ? color + "50" : "#E2D6C0"}`,
                    scrollSnapAlign: "start",
                  }}
                >
                  <span className="h-4 w-4 rounded-full flex items-center justify-center text-[8px] font-black text-white" style={{ background: color }}>{s.full_name.charAt(0)}</span>
                  {s.full_name.split(" ")[0]}
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.status_dot === "green" ? "#C4A882" : "#C4A882" }} />
                </button>
              );
            })}
            <button
              onClick={() => setShowAddChild(true)}
              className="flex items-center justify-center h-8 w-8 rounded-none shrink-0 transition-all hover:bg-[#EDE4D4]"
              style={{ border: "1px dashed #C4A882" }}
            >
              <PlusIcon className="h-3 w-3" style={{ color: "#7B1416" }} />
            </button>
          </nav>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════
          MOBILE SECTION SWITCHER — horizontal snap-scroll pills
          Visible on mobile/tablet only (bottom nav is the main nav,
          but this lets users reach all sections quickly)
         ═══════════════════════════════════════════════════════ */}
      <div
        className="lg:hidden sticky top-0 z-20 overflow-x-auto no-scrollbar"
        style={{
          background: "rgba(245,239,228,0.90)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
          borderBottom: "1px solid rgba(28,25,23,0.10)",
          scrollbarWidth: "none",
        }}
      >
        <div className="flex items-center gap-1.5 px-3 py-2.5" style={{ scrollSnapType: "x proximity" }}>
          {NAV_ITEMS.map(n => {
            const active = navSection === n.k;
            return (
              <button
                key={n.k}
                onClick={() => go(n.k)}
                className="flex items-center gap-1.5 rounded-none px-3 py-1.5 text-[11px] font-bold whitespace-nowrap transition-all"
                style={{
                  background: active ? "#1C1917" : "#EDE4D4",
                  color: active ? "#fff" : "#3C3530",
                  scrollSnapAlign: "start",
                }}
              >
                <n.icon className="h-3 w-3" style={{ color: active ? "#fff" : "#8B6F47" }} />
                {n.label}
                {n.badge ? (
                  <span className="h-3.5 min-w-3.5 rounded-none text-[8px] font-bold grid place-items-center px-1" style={{ background: active ? "rgba(196,168,130,0.3)" : "#7B1416", color: "#fff" }}>{n.badge}</span>
                ) : null}
              </button>
            );
          })}
          {students.map(s => {
            const color = childColor(students, s.id);
            const isActive = navSection === "child" && activeChildId === s.id;
            return (
              <button
                key={s.id}
                onClick={() => goChild(s.id)}
                className="flex items-center gap-1 rounded-none px-2.5 py-1.5 text-[11px] font-bold whitespace-nowrap transition-all"
                style={{
                  background: isActive ? `${color}15` : "#EDE4D4",
                  scrollSnapAlign: "start",
                }}
              >
                <span className="h-3.5 w-3.5 rounded-full flex items-center justify-center text-[7px] font-black text-white" style={{ background: color }}>{s.full_name.charAt(0)}</span>
                {s.full_name.split(" ")[0]}
              </button>
            );
          })}
          <button
            onClick={() => setShowAddChild(true)}
            className="flex items-center justify-center h-7 w-7 rounded-full shrink-0"
            style={{ border: "1px dashed rgba(123,20,22,0.25)" }}
          >
            <PlusIcon className="h-2.5 w-2.5" style={{ color: "#7B1416" }} />
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          CONTENT AREA — optimized padding for mobile PWA
         ═══════════════════════════════════════════════════════ */}
      <main className="relative mx-auto max-w-6xl px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-28 lg:pb-12" style={{ paddingBottom: "max(7rem, calc(2rem + env(safe-area-inset-bottom)))" }}>
        {showWizard && (
          <div className="mb-6">
            <LearnerOnboardingWizard guardianId={user?.id ?? "demo"} onDone={() => { setShowWizard(false); qc.invalidateQueries({ queryKey: ["students"] }); }} onCancel={() => setShowWizard(false)} />
          </div>
        )}
        <AddChildFromSchoolDialog open={showAddChild} onClose={() => setShowAddChild(false)} onChildAdded={() => qc.invalidateQueries({ queryKey: ["students"] })} />
        <AnimatePresence mode="wait">
          <motion.div
            key={navSection === "child" ? `child-${activeChildId}-${childTab}` : navSection}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            {navSection === "home" && <HomeView students={students} fees={fees} parentWallet={parentWallet} ledger={ledger} checkpoints={checkpoints} outstandingFees={outstandingFees} go={go} goChild={goChild} greetText={greetText} firstName={firstName} greetIcon={greetIcon} setActiveModal={setActiveModal} walletId={parentWallet?.id} onAddChild={() => setShowAddChild(true)} insights={insights} notifications={notifications} calendarEvents={calendarEvents} studentWallets={studentWallets} />}
            {navSection === "family" && <FamilyView students={students} fees={fees} checkpoints={checkpoints} visits={visits} goChild={goChild} firstName={firstName} />}
            {navSection === "child" && activeChild && <ChildWorkspace child={activeChild} tab={childTab} setTab={setChildTab} students={students} fees={fees} ledger={ledger} visits={visits} checkpoints={checkpoints} parentWallet={parentWallet} tabs={CHILD_TABS} />}
            {navSection === "finance" && <div className="space-y-6 sm:space-y-8"><FinanceSummary students={students} fees={fees} parentWallet={parentWallet} ledger={ledger} studentWallets={studentWallets} walletId={parentWallet?.id} /><MoneyView ledger={ledger} /></div>}
            {navSection === "messages" && <ParentMessages />}
            {navSection === "calendar" && <ParentCalendar students={students} />}
            {navSection === "marketplace" && <ParentCampusExchange />}
            {navSection === "documents" && <ParentDocuments />}
            {navSection === "consent" && <ConsentView students={students} />}
            {navSection === "settings" && <SettingsView user={user} firstName={firstName} />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* MOBILE BOTTOM NAV — premium with active pill + safe area */}
      <MobileBottomNav active={navSection} onChange={go} students={students} fees={fees} notifications={notifications} />

      {/* MODALS */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
            onClick={() => setActiveModal(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 24, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.92, y: 24, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className="w-full max-w-2xl rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              {activeModal === "timetable" && <TimetableModal onClose={() => setActiveModal(null)} />}
              {activeModal === "assignments" && <AssignmentsModal onClose={() => setActiveModal(null)} />}
              {activeModal === "message" && <MessageModal onClose={() => setActiveModal(null)} />}
              {activeModal === "wallet" && <WalletModal onClose={() => setActiveModal(null)} />}
              {activeModal === "listings" && <SavedListingsModal onClose={() => setActiveModal(null)} />}
              {activeModal === "events" && <EventsModal onClose={() => setActiveModal(null)} />}
              {activeModal === "report" && <ReportModal onClose={() => setActiveModal(null)} />}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileBottomNav({ active, onChange, students, fees, notifications }: {
  active: NavSection; onChange: (s: NavSection) => void; students: Student[]; fees: FeeTerm[]; notifications: any[];
}) {
  const items: { k: NavSection; label: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; badge?: number; color: string; gradient: string }[] = [
    { k: "home", label: "Home", icon: HomeIcon, color: "#1C1917", gradient: "#1C1917" },
    { k: "child", label: "Kids", icon: FamilyIcon, badge: students.length, color: "#1C1917", gradient: "#1C1917" },
    { k: "finance", label: "Finance", icon: FinanceIcon, badge: fees.filter(f => f.total_ugx > f.paid_ugx).length || undefined, color: "#1C1917", gradient: "#1C1917" },
    { k: "messages", label: "Inbox", icon: MessageIcon, badge: (notifications as any[] ?? []).filter((n: any) => !n.read_at).length || undefined, color: "#1C1917", gradient: "#1C1917" },
    { k: "settings", label: "More", icon: MenuIcon, color: "#1C1917", gradient: "#1C1917" },
  ];

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
        background: "linear-gradient(to top, rgba(255,255,255,1) 60%, rgba(255,255,255,0))",
      }}
    >
      <div className="px-3 pb-2 pt-3">
        <nav
          className="mx-auto max-w-md rounded-[20px] overflow-hidden"
          style={{
            background: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(48px) saturate(200%)",
            WebkitBackdropFilter: "blur(48px) saturate(200%)",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)",
          }}
        >
          <div className="flex items-stretch justify-around px-1.5 py-2">
            {items.map((item) => {
              const isActive = active === item.k || (item.k === "child" && (active === "family" || active === "child"));
              return (
                <motion.button
                  key={item.k}
                  onClick={() => onChange(item.k)}
                  whileTap={{ scale: 0.92 }}
                  transition={{ type: "spring", stiffness: 600, damping: 28 }}
                  className="relative flex flex-col items-center justify-center gap-1.5 flex-1 py-1.5 px-1 select-none"
                >
                  {/* Active background pill — animates in behind icon */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        layoutId="nav-active-bg"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ type: "spring", stiffness: 600, damping: 30 }}
                        className="absolute top-1.5 h-10 w-12 rounded-[10px]"
                        style={{ background: item.gradient, boxShadow: `0 4px 12px rgba(0,0,0,0.15)` }}
                      />
                    )}
                  </AnimatePresence>

                  {/* Icon — sits above label */}
                  <div className="relative z-10 h-6 w-6 flex items-center justify-center">
                    <motion.div
                      animate={{ scale: isActive ? 1.1 : 1, y: isActive ? -1 : 0 }}
                      transition={{ type: "spring", stiffness: 600, damping: 25 }}
                    >
                      <item.icon
                        className="h-[20px] w-[20px] transition-colors duration-200"
                        style={{ color: isActive ? "#fff" : "#8B6F47" }}
                      />
                    </motion.div>
                    {/* Badge */}
                    {item.badge ? (
                      <span
                        className="absolute -top-1 -right-2 h-[16px] min-w-[16px] rounded-full text-[9px] font-bold text-white grid place-items-center px-1"
                        style={{
                          background: "#B33A1A",
                          boxShadow: "0 0 0 2px rgba(255,255,255,0.95)",
                        }}
                      >
                        {item.badge}
                      </span>
                    ) : null}
                  </div>

                  {/* Label — sits below icon */}
                  <motion.span
                    animate={{
                      opacity: isActive ? 1 : 0.55,
                      fontWeight: isActive ? 700 : 500,
                    }}
                    transition={{ duration: 0.2 }}
                    className="relative z-10 text-[10px] tracking-tight leading-none transition-colors"
                    style={{ color: isActive ? "#fff" : "#8B6F47" }}
                  >
                    {item.label}
                  </motion.span>
                </motion.button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}

export default ParentDashboard;
